/**
 * Color Utility Functions
 *
 * Generates full color palettes from base colors for dynamic theming.
 * Used to create PrimeVue-compatible color scales from tenant configuration.
 */

export interface RGB {
  r: number
  g: number
  b: number
}

export type ShadeKey = '50' | '100' | '200' | '300' | '400' | '500' | '600' | '700' | '800' | '900' | '950'

export type ColorPalette = Record<ShadeKey, string>

function hexToRgb(hex: string): RGB {
  hex = hex.replace(/^#/, '')

  const bigint = parseInt(hex, 16)
  const r = (bigint >> 16) & 255
  const g = (bigint >> 8) & 255
  const b = bigint & 255

  return { r, g, b }
}

function rgbToHex(r: number, g: number, b: number): string {
  return (
    '#' +
    [r, g, b]
      .map((x) => {
        const hex = Math.round(x).toString(16)
        return hex.length === 1 ? '0' + hex : hex
      })
      .join('')
  )
}

function mixColors(color1: RGB, color2: RGB, amount: number): RGB {
  return {
    r: color1.r + (color2.r - color1.r) * amount,
    g: color1.g + (color2.g - color1.g) * amount,
    b: color1.b + (color2.b - color1.b) * amount
  }
}

function lighten(hex: string, amount: number): string {
  const rgb = hexToRgb(hex)
  const white: RGB = { r: 255, g: 255, b: 255 }
  const mixed = mixColors(rgb, white, amount)
  return rgbToHex(mixed.r, mixed.g, mixed.b)
}

function darken(hex: string, amount: number): string {
  const rgb = hexToRgb(hex)
  const black: RGB = { r: 0, g: 0, b: 0 }
  const mixed = mixColors(rgb, black, amount)
  return rgbToHex(mixed.r, mixed.g, mixed.b)
}

interface ShadeConfig {
  fn: (hex: string, amount: number) => string
  amount: number
}

export function generateColorPalette(baseHex: string): ColorPalette {
  if (!baseHex.startsWith('#')) {
    baseHex = '#' + baseHex
  }

  const shadeConfig: Record<ShadeKey, ShadeConfig | null> = {
    '50': { fn: lighten, amount: 0.95 },
    '100': { fn: lighten, amount: 0.9 },
    '200': { fn: lighten, amount: 0.75 },
    '300': { fn: lighten, amount: 0.5 },
    '400': { fn: lighten, amount: 0.25 },
    '500': null,
    '600': { fn: darken, amount: 0.15 },
    '700': { fn: darken, amount: 0.3 },
    '800': { fn: darken, amount: 0.5 },
    '900': { fn: darken, amount: 0.65 },
    '950': { fn: darken, amount: 0.8 }
  }

  const palette = {} as ColorPalette

  for (const [shade, config] of Object.entries(shadeConfig) as [ShadeKey, ShadeConfig | null][]) {
    if (config === null) {
      palette[shade] = baseHex
    } else {
      palette[shade] = config.fn(baseHex, config.amount)
    }
  }

  return palette
}

export function isValidHex(hex: string | null | undefined): boolean {
  if (!hex) return false

  const cleanHex = hex.replace(/^#/, '')

  return /^[0-9A-Fa-f]{3}$|^[0-9A-Fa-f]{6}$/.test(cleanHex)
}

export function normalizeHex(hex: string | null | undefined): string {
  if (!hex) return '#000000'

  hex = hex.trim()

  if (!hex.startsWith('#')) {
    hex = '#' + hex
  }

  if (hex.length === 4) {
    hex = '#' + hex[1] + hex[1] + hex[2] + hex[2] + hex[3] + hex[3]
  }

  return hex
}

export function getContrastingTextColor(backgroundHex: string): string {
  const rgb = hexToRgb(backgroundHex)

  const luminance = (0.299 * rgb.r + 0.587 * rgb.g + 0.114 * rgb.b) / 255

  return luminance > 0.5 ? '#000000' : '#ffffff'
}
